import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

/*
  This example requires some changes to your config:

  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ]
  }
  ```
*/

export const query = graphql`
  fragment HeroWithSignInFormData on StrapiComponentHeroWithSignInForm {
    title
    topic
    description
    referenceText
    referenceLogos {
      ...StrapiSharedLink
    }
  }
`;

function WithSignInForm({ data, children }) {
  if (!data) {
    return <template />;
  }
  return (
    <div className="relative overflow-hidden bg-background-primary-dark">
      <div
        className="hidden sm:absolute sm:inset-0 sm:block"
        aria-hidden="true"
      >
        <svg
          className="absolute bottom-0 right-0 mb-48 translate-x-1/2 transform text-text-dark-gray lg:top-0 lg:mb-0 lg:mt-28 xl:translate-x-0 xl:transform-none"
          width={364}
          height={384}
          viewBox="0 0 364 384"
          fill="none"
        >
          <defs>
            <pattern
              id="eab71dd9-9d7a-47bd-8044-256344ee00d0"
              x={0}
              y={0}
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
            >
              <rect x={0} y={0} width={4} height={4} fill="currentColor" />
            </pattern>
          </defs>
          <rect
            width={364}
            height={384}
            fill="url(#eab71dd9-9d7a-47bd-8044-256344ee00d0)"
          />
        </svg>
      </div>
      <div className="relative pb-16 pt-6 sm:pb-24">
        <main className="mt-16 sm:mt-24">
          <div className="mx-auto max-w-7xl">
            <div className="lg:grid lg:grid-cols-12 lg:gap-8">
              <div className="px-4 sm:px-6 sm:text-center md:mx-auto md:max-w-2xl lg:col-span-6 lg:flex lg:items-center lg:text-left">
                <div>
                  {data.topic && (
                    <h3 className="mt-4 text-2xl tracking-tight text-text-white sm:mt-5 sm:leading-none lg:mt-6 lg:text-3xl">
                      <span className="md:block">
                        <MarkdownWrapper text={data.topic} />
                      </span>
                    </h3>
                  )}
                  {data.title && (
                    <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-text-white sm:mt-5 sm:leading-none lg:mt-6 lg:text-5xl xl:text-6xl">
                      <span className="md:block">
                        <MarkdownWrapper text={data.title} />
                      </span>
                    </h1>
                  )}
                  {data.description && (
                    <MarkdownWrapper
                      text={data.description}
                      markdownClassName="mt-3 text-base text-text-secondary-light sm:mt-5 sm:text-xl lg:text-lg xl:text-xl"
                      replaceParagraph={false}
                    />
                  )}
                  {data.referenceText && (
                    <MarkdownWrapper
                      text={data.referenceText}
                      markdownClassName="mt-8 text-sm text-text-white uppercase tracking-wide font-semibold sm:mt-10"
                    />
                  )}
                  {Boolean(data.referenceLogos?.length) && (
                    <div className="mt-5 w-full sm:mx-auto sm:max-w-lg lg:ml-0">
                      <div className="flex flex-wrap items-start justify-between">
                        {data.referenceLogos.map(
                          (logo, index) =>
                            (logo.overwriteIcon || logo.link?.icon) && (
                              <div
                                key={index}
                                className="flex justify-center px-1"
                              >
                                <LinkWrapper
                                  to={logo?.link?.url}
                                  className={`px-4 py-2 ${
                                    logo?.link?.url
                                      ? "rounded-md hover:bg-background-light-gray"
                                      : ""
                                  }`}
                                >
                                  <MediaWrapper
                                    media={
                                      logo.overwriteIcon || logo.link?.icon
                                    }
                                    divClassName="h-9 sm:h-10"
                                    objectFit="contain"
                                  />
                                </LinkWrapper>
                              </div>
                            )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

WithSignInForm.propTypes = {
  data: PropTypes.object,
  children: PropTypes.object,
};

export default WithSignInForm;
